import { Box, Flex } from '@chakra-ui/react';
import { NON_DIGIT_GLOBAL_REGEX } from '@/constants/regex';
import { Input } from '@kula-ai/components';
import { Minus, Plus } from '@kula-ai/icons-core';
import type { ChangeEvent } from 'react';
import { useField } from 'formik';
import { KText } from '@/ui-library/components/text';

interface NumberStepperProps {
  item?: Record<string, unknown>;
  field: {
    name: string;
    value: string | number | undefined;
    onBlur: (_event: React.FocusEvent<HTMLInputElement>) => void;
  };
}

const parseNumber = (value: string | number | undefined) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const NumberStepper = ({ item = {}, field }: NumberStepperProps) => {
  // Some callers pass step/min/max at the top level (e.g. settings/buffer
  // section), others nest them under `fieldProps` — the convention the
  // kula-ai NumberStepperAdapter and `useDurationFieldConfig` follow.
  // Read both shapes so the local FormBuilder and the kula-ai FormBuilder
  // honour the same config; otherwise `useDurationFieldConfig`'s
  // `fieldProps.min: 1` is silently ignored here and the floor stays at 15.
  const fieldProps = (item as { fieldProps?: Record<string, unknown> })
    .fieldProps;
  const {
    name,
    step: stepRaw,
    min: minRaw,
    max: maxRaw,
    placeholder = '0',
    unitLabel: unitLabelRaw,
    onChange: onItemChange,
  } = item as {
    name: string;
    step?: number;
    min?: number;
    max?: number;
    placeholder?: string;
    unitLabel?: string;
    onChange?: (_next: number) => void;
  };
  const step = stepRaw ?? (fieldProps?.step as number | undefined) ?? 15;
  const min = minRaw ?? (fieldProps?.min as number | undefined) ?? 1;
  const max = maxRaw ?? (fieldProps?.max as number | undefined) ?? 1440;
  const unitLabel =
    unitLabelRaw ?? (fieldProps?.unitLabel as string | undefined) ?? 'mins';
  const onChange = onItemChange ?? (() => {});

  const [, , helpers] = useField(name);
  const currentValue = parseNumber(field?.value as string | number | undefined);

  const setSafeValue = (nextValue: number) => {
    const boundedValue = Math.min(max, Math.max(min, nextValue));
    helpers?.setValue?.(boundedValue);
    onChange?.(boundedValue);
  };

  const onDecrease = () => {
    setSafeValue(currentValue - step);
  };

  const onIncrease = () => {
    setSafeValue(currentValue + step);
  };

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;

    // allow empty freely
    if (rawValue === '') {
      helpers?.setValue?.('');
      return;
    }

    // allow only digits but DON'T clamp
    const sanitized = rawValue.replace(NON_DIGIT_GLOBAL_REGEX, '');

    helpers?.setValue?.(sanitized);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    field.onBlur(e);

    const parsed = parseNumber(field.value);

    // fallback if empty
    if (field.value === '' || field.value === undefined) {
      setSafeValue(min);
      return;
    }

    setSafeValue(parsed);
  };

  const isAtMin = currentValue <= min;
  const isAtMax = currentValue >= max;

  const leftControl = (
    <Box
      as="button"
      type="button"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p="10px 11px"
      ml="-12px"
      borderLeftRadius="6px"
      borderRight="1px solid"
      borderColor="secondary-100"
      bg="secondary-25"
      _hover={{ bg: 'secondary-100', borderColor: 'secondary-200' }}
      cursor={isAtMin ? 'not-allowed' : 'pointer'}
      opacity={isAtMin ? 0.4 : 1}
      aria-label="Decrease value"
      disabled={isAtMin}
      onClick={(e) => {
        e.preventDefault();
        if (!isAtMin) onDecrease();
      }}
    >
      <Flex gap="3px" alignItems="center" justifyContent="center">
        <Minus size={16} />
        {step}
      </Flex>
    </Box>
  );

  const rightControl = (
    <Flex alignItems="center" gap="8px">
      {unitLabel ? (
        <KText size="sm-regular" color="secondary-600">
          {unitLabel}
        </KText>
      ) : null}
      <Box
        as="button"
        type="button"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="secondary-25"
        p="10px 11px"
        mr="-12px"
        borderRightRadius="6px"
        borderLeft="1px solid"
        borderColor="secondary-100"
        _hover={{ bg: 'secondary-100', borderColor: 'secondary-200' }}
        cursor={isAtMax ? 'not-allowed' : 'pointer'}
        opacity={isAtMax ? 0.4 : 1}
        aria-label="Increase value"
        disabled={isAtMax}
        onClick={(e) => {
          e.preventDefault();
          if (!isAtMax) onIncrease();
        }}
      >
        <Flex gap="3px" alignItems="center" justifyContent="center">
          <Plus size={16} />
          {step}
        </Flex>
      </Box>
    </Flex>
  );

  return (
    <Input
      name={field.name}
      value={
        field?.value === '' ||
        field?.value === undefined ||
        field?.value === null
          ? ''
          : String(field.value)
      }
      onChange={onInputChange}
      onBlur={onBlur}
      placeholder={placeholder}
      type="text"
      size="md"
      leftElement={leftControl}
      rightElement={rightControl}
      style={{ textAlign: 'left' }}
    />
  );
};
